import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiMapPin,
} from "react-icons/fi";

import { axiosClient } from "../client/http-client";
import { getAuthToken } from "../client/token-utils";
import { useMutation } from "@tanstack/react-query";

import { MotionReveal, WizardProgress, WizardTitle, toWizardSteps } from "./ui";
import ApplicationStartError from "./applicationStartError";

// steps
import GeneralStep from "./RegisterBranch/ApplicationSteps/General/generalStep";
import LicenseStep from "./RegisterBranch/ApplicationSteps/License/licenseStep";
import ContactsStep from "./RegisterBranch/ApplicationSteps/Contacts/contactsStep";
import UploadsStep from "./RegisterBranch/ApplicationSteps/Uploads/uploadsStep";
import SignatureStep from "./RegisterBranch/ApplicationSteps/Signature/signatureStep";
import RequestsStep from "./RegisterBranch/ApplicationSteps/Requests/requestsStep";

import {
  GeneralInfo,
  ContactsInfo,
  LicenseInfo,
  Upload,
  Requests,
  Signatures,
} from "../types/registerBranchDataTypes";

import { useStepTimer } from "../utils/timer";

import {
  validateGeneralStep,
  validateContactsStep,
  validateUploadsStep,
} from "./RegisterBranch/stepsValidators/validators";

interface registerBranchApplicationModalProps {
  isOpen: boolean;
  OnOpen: () => void;
  onClose: () => void;
  applicationData: any;
  refetchApplication: () => void;
  lookingForActiveApplications: boolean;
  isApplicationActive: boolean;
  setIsApplicationActive: (status: boolean) => void;
  refetchApplications: () => void;
  /** Ref to the choice-card trigger so the parent modal can give it initial focus. */
  triggerRef?: React.Ref<HTMLButtonElement>;
}

// modal steps, it consists of 3 steps each step contains multi steps
const modalSteps: { [key: string]: string[] } = {
  general: ["general", "license"],
  contacts: ["contacts"],
  uploads: ["uploads", "signatures", "requests"],
};

// get the main 3 steps names to use in the stepper
const mainSteps = Object.keys(modalSteps);

/** Sub-group heading inside a wizard step: h3 element (xs/h4 look, sits under the h2 wizard title) + hairline (§7.4). */
const StepGroup: React.FC<{
  title: string;
  lede?: string;
  children: React.ReactNode;
}> = ({ title, lede, children }) => (
  <Box as="section">
    <Flex align="baseline" gap={3} wrap="wrap">
      <Heading as="h3" size="xs">
        {title}
      </Heading>
      {lede && (
        <Text textStyle="body.sm" color="text.muted">
          {lede}
        </Text>
      )}
    </Flex>
    <Divider mt={3} mb={5} />
    {children}
  </Box>
);

const RegisterBranchApplicationModal: React.FC<
  registerBranchApplicationModalProps
> = ({
  OnOpen,
  isOpen,
  onClose,
  applicationData,
  refetchApplication,
  lookingForActiveApplications,
  isApplicationActive,
  setIsApplicationActive,
  refetchApplications,
  triggerRef,
}) => {
  // get user token from cookies
  const token = getAuthToken();

  // current active step
  const [activeStep, setActiveStep] = useState(0);

  // the steps if the active step
  const currentSteps = modalSteps[mainSteps[activeStep]];

  // toast to view notifications
  const toast = useToast();

  // timer hook to calculate time spent on each step
  const { stopTimer, startTimer } = useStepTimer(isOpen, activeStep);

  // first field of the first step — receives focus when the modal opens
  const firstFieldRef = useRef<HTMLInputElement>(null);

  // state to handle the uploaded files
  const [documents_info, setDocumentsInfo] = useState<Upload>({
    license: undefined,
    owner_pp: undefined,
    owner_eid: undefined,
    hasVisa: "no",
    owner_visa: undefined,
    hasPowerOfAttorney: "no",
    power_of_attorney: undefined,
    hasVatCert: "no",
  });

  // data of the active applications
  const [stepsData, setStepsData] = useState<any>({});

  // state to check if form data saved
  const [isStepSaved, setIsStepSaved] = useState<boolean>(false);

  // Effect to set the steps data when data is available
  useEffect(() => {
    if (applicationData) {
      setStepsData({
        general: applicationData.general_info,
        contacts: applicationData.contacts_info,
        license: applicationData.license_info,
        uploads: applicationData.documents_info,
        signatures: applicationData.authorised_signatures_info,
        requests: applicationData.requests_info,
      });
    }
  }, [applicationData]);

  useEffect(() => {
    setIsStepSaved(false);

    if (mainSteps[activeStep] === "general") {
      if (
        validateGeneralStep({
          general: stepsData["general"],
          license: stepsData["license"],
        })
      ) {
        setIsStepSaved(true);
      }
    } else if (mainSteps[activeStep] === "contacts") {
      if (
        validateContactsStep({
          contacts: stepsData["contacts"],
        })
      ) {
        setIsStepSaved(true);
      }
    } else {
      if (
        validateUploadsStep({
          signatures: stepsData["signatures"],
          requests: stepsData["requests"],
        })
      ) {
        setIsStepSaved(true);
      }
    }
  }, [activeStep, stepsData]);

  //1- function create a register branch application
  const createRegisterBranchApplication = async () => {
    return await axiosClient.post(
      "/applications/register-branch/create",
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  //1-- use mutation for register branch application creation
  const {
    mutate: mutateRegisterBranchApplication,
    isPending: isCreatingApplication,
    error: createApplicationError,
  } = useMutation({
    mutationKey: ["registerBranchApplication"],
    mutationFn: createRegisterBranchApplication,
    onSuccess: (data) => {
      setIsApplicationActive(true);
      OnOpen();
      toast({
        title: data.data.message,
        status: data.status === 200 ? "warning" : "success",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  // time spent updating
  // 2- function to update the time spent on each step on backend
  const updateSpentTime = async (time: number) => {
    return await axiosClient.post(
      `/applications/register-branch/update-time/${mainSteps[activeStep]}`,
      { time_spent: time },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  //2-- use mutation for updating time spent
  const { mutate: updateTimeSpent, isPending: isUpdatingTime } = useMutation({
    mutationKey: ["registerBranchApplication-Time" + mainSteps[activeStep]],
    mutationFn: updateSpentTime,

    onError: (error: { response: { data: { message: [string] } } }) => {
      if (typeof error.response.data.message === "string") {
        return toast({
          title: error.response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        error.response.data.message.forEach((message: string) => {
          toast({
            title: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
      }
    },
  });

  // data updating
  //3- function to update the data of each step
  const submitData = async () => {
    const reqResponse = await Promise.all(
      currentSteps.map(async (step) => {
        if (stepsData[step]) {
          const response = await axiosClient.post(
            `/applications/register-branch/save-progress/${step}`,
            step === "uploads" ? documents_info : stepsData[step],
            {
              headers: {
                // handle file uploads in uploads step only
                "Content-Type":
                  step === "uploads"
                    ? "multipart/form-data"
                    : "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          return response.data;
        }
        return null; // In case no applicationData for a step, return null
      })
    );

    // Filter out any null responses in case there were steps without applicationData
    return reqResponse.filter((res) => res !== null);
  };

  //3-- use mutation for updating steps data
  const { mutate, isPending: isMutating } = useMutation({
    mutationKey: ["registerBranchApplication" + mainSteps[activeStep]],
    mutationFn: submitData,

    onSuccess: (data) => {
      const timeSpent = stopTimer(); // Call stopTimer and get the time spent on the current step
      updateTimeSpent(timeSpent);
      setIsStepSaved(true);


      // if the data.message on each response in the array were the same return only one
      if (data.every((response: any) => response.message === data[0].message)) {
        toast({
          title: data[0].message,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        data.forEach((response: any) => {
          toast({
            title: response.data.message,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        });
      }

      refetchApplication();
    },
    onError: (error: { response: { data: { message: [string] } } }) => {
      console.log("error", error);
      if (typeof error.response.data.message === "string") {
        return toast({
          title: error.response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        error.response.data.message.forEach((message: string) => {
          toast({
            title: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
      }
    },
  });

  // application finishing
  // 4- function to handle the application finshing
  const FinishApplication = async () => {
    return await axiosClient.post(
      `/applications/register-branch/finish`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  //4-- use mutate to handle the application finish
  const { mutate: finishApplication, isPending: isFinishing } = useMutation({
    mutationKey: ["registerBranchApplication-Finish"],
    mutationFn: FinishApplication,
    onSuccess: (data) => {
      setIsApplicationActive(false);
      // empty the stepsData
      setStepsData({});

      // remove uploaded files
      setDocumentsInfo({
        license: undefined,
        owner_pp: undefined,
        owner_eid: undefined,
        hasVisa: "no",
        owner_visa: undefined,
        hasPowerOfAttorney: "no",
        power_of_attorney: undefined,
        hasVatCert: "no",
      });

      // reset the currentStep to 0
      setActiveStep(0);

      // send notification
      toast({
        title: data.data.message,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      refetchApplication();
      refetchApplications();
      onClose();
    },
    onError: (error: { response: { data: { message: [string] } } }) => {
      if (typeof error.response.data.message === "string") {
        return toast({
          title: error.response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        error.response.data.message.forEach((message: string) => {
          toast({
            title: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        });
      }
    },
  });

  // movement between steps
  // Function to handle moving to the next step
  const nextStep = () => {
    if (activeStep < mainSteps.length - 1) {
      setActiveStep(activeStep + 1); // Move to the next step
      startTimer(); // Start timing the new step
    }
  };

  // Function to handle moving to the previous step
  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1); // Move to the previous step
      startTimer(); // Restart the timer for the previous step
    }
  };

  const handleSubmitData = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    mutate();
  };

  // Function to validate if the current step data is complete
  const isCurrentStepValid = () => {
    switch (activeStep) {
      case 0:
        return validateGeneralStep({
          general: stepsData["general"],
          license: stepsData["license"],
        });
      case 1:
        return validateContactsStep({ contacts: stepsData["contacts"] });
      case 2:
        return validateUploadsStep({
          signatures: stepsData["signatures"],
          requests: stepsData["requests"],
        });
      default:
        return false;
    }
  };

  return (
    <>
      {/* Choice card trigger (§7.4) — rendered by createApplicationModal inside its Stack */}
      <Button
        ref={triggerRef}
        variant="secondary"
        h="auto"
        w="full"
        p={5}
        textAlign="left"
        justifyContent="flex-start"
        whiteSpace="normal"
        borderRadius="lg"
        data-group
        _hover={{
          boxShadow: "0 0 0 1.5px var(--chakra-colors-leaf-500)",
          bg: "leaf.50",
          _focusVisible: { boxShadow: "outline" },
          _disabled: {
            boxShadow: "0 0 0 1px var(--chakra-colors-border-strong)",
            bg: "surface.card",
          },
        }}
        // Busy state keeps the card content mounted (no isLoading swap) so the card never collapses to a pill.
        isDisabled={isCreatingApplication || lookingForActiveApplications || isApplicationActive}
        aria-busy={isCreatingApplication || lookingForActiveApplications}
        onClick={() => {
          mutateRegisterBranchApplication();
        }}
      >
        {/* Spans only inside the <button> (phrasing content); display is set explicitly. */}
        <HStack as="span" display="flex" spacing={4} w="full" align="center">
          <Flex
            as="span"
            display="flex"
            w="44px"
            h="44px"
            flexShrink={0}
            borderRadius="md"
            bg="navy.50"
            color="navy.700"
            align="center"
            justify="center"
            aria-hidden
          >
            <Icon as={FiMapPin} boxSize="20px" />
          </Flex>
          <Box as="span" display="block" flex={1} minW={0}>
            <Text as="span" display="block" textStyle="h3" color="text.primary">
              Register a new branch
            </Text>
            <Text as="span" display="block" textStyle="body.sm" color="text.muted" fontWeight={500} mt={0.5}>
              {isCreatingApplication
                ? "Creating application…"
                : lookingForActiveApplications
                  ? "Checking for an active application…"
                  : "Set up a new outlet under your account — outlet details, contacts and documents."}
            </Text>
          </Box>
          {isCreatingApplication || lookingForActiveApplications ? (
            <Spinner as="span" size="sm" color="text.muted" flexShrink={0} aria-hidden />
          ) : (
            <Icon
              as={FiArrowRight}
              boxSize="18px"
              color="text.muted"
              flexShrink={0}
              aria-hidden
              transition="transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1)"
              _groupHover={{ transform: "translateX(2px)", color: "navy.900" }}
            />
          )}
        </HStack>
      </Button>
      {createApplicationError && <ApplicationStartError error={createApplicationError} />}

      <Modal
        scrollBehavior="inside"
        closeOnOverlayClick={false}
        size={"4xl"}
        isCentered
        onClose={onClose}
        isOpen={isOpen}
        motionPreset="slideInBottom"
        // The first-field ref only exists on step 0; on later steps focus goes to the first field of the
        // step body (data-autofocus-inside on ModalBody) instead of the close button.
        initialFocusRef={activeStep === 0 ? firstFieldRef : undefined}
      >
        <ModalOverlay />
        <ModalContent as={"form"} onSubmit={handleSubmitData}>
          <ModalHeader p={0} border="none">
            <WizardTitle eyebrow="NEW APPLICATION" title="Register a branch" />
          </ModalHeader>
          <ModalCloseButton aria-label="Close" />
          <WizardProgress steps={toWizardSteps(mainSteps)} activeStep={activeStep} isStepSaved={isStepSaved} />
          <ModalBody px={{ base: 5, md: 8 }} py={6} data-autofocus-inside>
            {/* render the components of the currentSteps — remount on step change for the fade-up */}
            <MotionReveal key={activeStep}>
              {activeStep === 0 && (
                <Stack spacing={10}>
                  <StepGroup
                    title="General information"
                    lede="Outlet identity, billing and delivery addresses"
                  >
                    <GeneralStep
                      savedData={stepsData.general ?? ({} as GeneralInfo)}
                      updateData={(data: GeneralInfo) => {
                        setStepsData({
                          ...stepsData,
                          general: data,
                        });
                      }}
                      initialFocusRef={firstFieldRef}
                    />
                  </StepGroup>

                  <StepGroup
                    title="Trade licence & VAT"
                    lede="As printed on your trade licence"
                  >
                    <LicenseStep
                      savedData={stepsData.license ?? ({} as LicenseInfo)}
                      updateData={(data: LicenseInfo) => {
                        setStepsData({
                          ...stepsData,
                          license: data,
                        });
                      }}
                    />
                  </StepGroup>
                </Stack>
              )}

              {activeStep === 1 && (
                <StepGroup
                  title="Contacts"
                  lede="Who we should reach at this branch"
                >
                  <ContactsStep
                    savedData={stepsData.contacts ?? ([] as ContactsInfo)}
                    updateData={(data: ContactsInfo) => {
                      setStepsData({
                        ...stepsData,
                        contacts: data,
                      });
                    }}
                  />
                </StepGroup>
              )}

              {activeStep === 2 && (
                <Stack spacing={10}>
                  <StepGroup
                    title="Documents"
                    lede="Trade licence, identity and supporting papers"
                  >
                    <UploadsStep
                      savedData={
                        stepsData.uploads ??
                        ([] as {
                          fieldname: string;
                          originalname: string;
                        }[])
                      }
                      documents_info={documents_info ?? ({} as Upload)}
                      setDocumentsInfo={setDocumentsInfo}
                    />
                  </StepGroup>

                  <StepGroup
                    title="Authorised signatures"
                    lede="People who may sign on behalf of the branch"
                  >
                    <SignatureStep
                      savedData={stepsData.signatures ?? ([] as Signatures)}
                      updateData={(data: Signatures) => {
                        setStepsData({
                          ...stepsData,
                          signatures: data,
                        });
                      }}
                    />
                  </StepGroup>

                  <StepGroup
                    title="Credit request"
                    lede="Limit, period and how you will pay"
                  >
                    <RequestsStep
                      savedData={stepsData.requests ?? ({} as Requests)}
                      updateData={(data: Requests) => {
                        setStepsData({
                          ...stepsData,
                          requests: data,
                        });
                      }}
                    />
                  </StepGroup>
                </Stack>
              )}
            </MotionReveal>
          </ModalBody>
          <ModalFooter flexWrap="wrap">
            <Button
              variant="ghost"
              leftIcon={<FiArrowLeft />}
              onClick={prevStep}
              isDisabled={activeStep === 0}
            >
              Back
            </Button>

            <HStack spacing={3} flexWrap="wrap" justify="flex-end" ml="auto">
              <Button
                variant="secondary"
                type="submit"
                isDisabled={activeStep >= mainSteps.length}
                isLoading={isMutating || isUpdatingTime}
              >
                Save progress
              </Button>

              {activeStep === mainSteps.length - 1 ? (
                <Button
                  variant="primary"
                  leftIcon={<FiCheckCircle />}
                  isLoading={isFinishing}
                  onClick={() => {
                    finishApplication();
                  }}
                  isDisabled={!(isCurrentStepValid() && isStepSaved)}
                >
                  Submit application
                </Button>
              ) : (
                <Button
                  variant="primary"
                  rightIcon={<FiArrowRight />}
                  onClick={nextStep}
                  isDisabled={!(isCurrentStepValid() && isStepSaved)}
                >
                  Next
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RegisterBranchApplicationModal;
